import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";

import {
  Close,
  HelpOutline,
  Launch,
  PlayCircleOutline,
  Search
} from "@material-ui/icons";

import MainContainer
  from "../../components/MainContainer";

import MainHeader
  from "../../components/MainHeader";

import Title
  from "../../components/Title";

import useHelps
  from "../../hooks/useHelps";

const useStyles =
  makeStyles(theme => ({
    hero: {
      padding:
        theme.spacing(3),
      marginBottom:
        theme.spacing(3),
      borderRadius: 14,
      background:
        theme.palette.background.paper,
      border:
        `1px solid ${theme.palette.divider}`
    },

    heroTitle: {
      fontWeight: 700,
      marginBottom:
        theme.spacing(1)
    },

    heroText: {
      color:
        theme.palette.text.secondary,
      marginBottom:
        theme.spacing(2)
    },

    search: {
      maxWidth: 720
    },

    categories: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginBottom:
        theme.spacing(3)
    },

    card: {
      height: "100%",
      padding:
        theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      borderRadius: 14,
      border:
        `1px solid ${theme.palette.divider}`,
      transition:
        "transform .18s ease, box-shadow .18s ease"
    },

    cardHover: {
      "&:hover": {
        transform:
          "translateY(-2px)",
        boxShadow:
          theme.shadows[4]
      }
    },

    thumbnail: {
      width: "100%",
      height: 170,
      objectFit: "cover",
      borderRadius: 10,
      cursor: "pointer",
      marginBottom:
        theme.spacing(2)
    },

    noVideo: {
      width: "100%",
      height: 170,
      borderRadius: 10,
      marginBottom:
        theme.spacing(2),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        theme.palette.action.hover
    },

    cardCategory: {
      marginBottom:
        theme.spacing(1)
    },

    cardTitle: {
      fontWeight: 700,
      marginBottom:
        theme.spacing(1)
    },

    cardDescription: {
      color:
        theme.palette.text.secondary,
      whiteSpace: "pre-line",
      marginBottom:
        theme.spacing(2),
      flex: 1
    },

    actions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: "auto"
    },

    empty: {
      padding:
        theme.spacing(5),
      textAlign: "center",
      borderRadius: 14
    },

    emptyIcon: {
      fontSize: 52,
      opacity: 0.45,
      marginBottom:
        theme.spacing(1)
    },

    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding:
        theme.spacing(2)
    },

    modalContent: {
      width: "90%",
      maxWidth: 1050,
      background:
        theme.palette.background.paper,
      borderRadius: 12,
      outline: "none",
      overflow: "hidden",
      boxShadow:
        theme.shadows[8]
    },

    modalHeader: {
      display: "flex",
      justifyContent:
        "space-between",
      alignItems: "center",
      padding:
        theme.spacing(1, 2)
    },

    videoWrapper: {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%"
    },

    iframe: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      border: 0
    }
  }));

const normalize =
  value =>
    String(
      value || ""
    )
      .toLowerCase()
      .normalize("NFD")
      .replace(
        /[\u0300-\u036f]/g,
        ""
      );

const Helps = () => {
  const classes =
    useStyles();

  const {
    list
  } =
    useHelps();

  const [
    records,
    setRecords
  ] =
    useState([]);

  const [
    search,
    setSearch
  ] =
    useState("");

  const [
    selectedCategory,
    setSelectedCategory
  ] =
    useState("Todas");

  const [
    selectedVideo,
    setSelectedVideo
  ] =
    useState(null);

  useEffect(() => {
    let mounted = true;

    const load =
      async () => {
        try {
          const data =
            await list();

          if (mounted) {
            setRecords(
              Array.isArray(data)
                ? data
                : []
            );
          }
        } catch (error) {
          if (mounted) {
            setRecords([]);
          }
        }
      };

    load();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line
  }, []);

  const categories =
    useMemo(() => {
      const values =
        records
          .map(
            item =>
              item.category ||
              "Geral"
          )
          .filter(Boolean);

      return [
        "Todas",
        ...Array.from(
          new Set(values)
        ).sort(
          (a, b) =>
            a.localeCompare(
              b,
              "pt-BR"
            )
        )
      ];
    }, [records]);

  const filteredRecords =
    useMemo(() => {
      const query =
        normalize(search);

      return records
        .filter(record => {
          const category =
            record.category ||
            "Geral";

          if (
            selectedCategory !==
              "Todas" &&
            category !==
              selectedCategory
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          const haystack =
            normalize(
              [
                record.title,
                record.description,
                record.category,
                record.route
              ].join(" ")
            );

          return haystack.includes(
            query
          );
        })
        .sort((a, b) => {
          const orderA =
            Number(
              a.sortOrder || 0
            );

          const orderB =
            Number(
              b.sortOrder || 0
            );

          if (
            orderA !== orderB
          ) {
            return orderA - orderB;
          }

          return String(
            a.title || ""
          ).localeCompare(
            String(
              b.title || ""
            ),
            "pt-BR"
          );
        });
    }, [
      records,
      search,
      selectedCategory
    ]);

  const openVideo =
    record => {
      if (!record.video) {
        return;
      }

      setSelectedVideo(
        record
      );
    };

  const openLink =
    link => {
      if (!link) {
        return;
      }

      window.open(
        link,
        "_blank",
        "noopener,noreferrer"
      );
    };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          Central de Ajuda
        </Title>
      </MainHeader>

      <Paper
        elevation={0}
        className={
          classes.hero
        }
      >
        <Typography
          variant="h5"
          className={
            classes.heroTitle
          }
        >
          Como podemos ajudar?
        </Typography>

        <Typography
          variant="body1"
          className={
            classes.heroText
          }
        >
          Pesquise uma função do
          Ventura Atende ou escolha
          uma categoria para ver
          explicações e vídeo-aulas.
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ex.: conectar WhatsApp, criar usuário, financeiro..."
          value={search}
          onChange={event =>
            setSearch(
              event.target.value
            )
          }
          className={
            classes.search
          }
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
              >
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {categories.length >
        1 && (
        <div
          className={
            classes.categories
          }
        >
          {categories.map(
            category => (
              <Chip
                key={category}
                label={category}
                clickable
                color={
                  selectedCategory ===
                  category
                    ? "primary"
                    : "default"
                }
                onClick={() =>
                  setSelectedCategory(
                    category
                  )
                }
              />
            )
          )}
        </div>
      )}

      {filteredRecords.length >
        0 ? (
        <Grid
          container
          spacing={3}
        >
          {filteredRecords.map(
            record => (
              <Grid
                key={record.id}
                item
                xs={12}
                sm={6}
                md={4}
              >
                <Paper
                  elevation={1}
                  className={
                    `${classes.card} ${classes.cardHover}`
                  }
                >
                  {record.video ? (
                    <img
                      src={
                        `https://img.youtube.com/vi/${record.video}/mqdefault.jpg`
                      }
                      alt={
                        record.title ||
                        "Vídeo de ajuda"
                      }
                      className={
                        classes.thumbnail
                      }
                      onClick={() =>
                        openVideo(
                          record
                        )
                      }
                    />
                  ) : (
                    <div
                      className={
                        classes.noVideo
                      }
                    >
                      <HelpOutline
                        style={{
                          fontSize: 58,
                          opacity: 0.45
                        }}
                      />
                    </div>
                  )}

                  <div
                    className={
                      classes.cardCategory
                    }
                  >
                    <Chip
                      size="small"
                      label={
                        record.category ||
                        "Geral"
                      }
                    />
                  </div>

                  <Typography
                    variant="h6"
                    className={
                      classes.cardTitle
                    }
                  >
                    {record.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    className={
                      classes.cardDescription
                    }
                  >
                    {record.description ||
                      "Consulte este conteúdo para saber mais sobre esta função."}
                  </Typography>

                  <div
                    className={
                      classes.actions
                    }
                  >
                    {record.video && (
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        startIcon={
                          <PlayCircleOutline />
                        }
                        onClick={() =>
                          openVideo(
                            record
                          )
                        }
                      >
                        Ver vídeo
                      </Button>
                    )}

                    {record.link && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          <Launch />
                        }
                        onClick={() =>
                          openLink(
                            record.link
                          )
                        }
                      >
                        Abrir link
                      </Button>
                    )}
                  </div>
                </Paper>
              </Grid>
            )
          )}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          className={
            classes.empty
          }
        >
          <HelpOutline
            className={
              classes.emptyIcon
            }
          />

          <Typography
            variant="h6"
            gutterBottom
          >
            Nenhum conteúdo encontrado
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
          >
            Tente pesquisar por outro
            termo ou escolha outra
            categoria.
          </Typography>
        </Paper>
      )}

      <Modal
        open={
          Boolean(
            selectedVideo
          )
        }
        onClose={() =>
          setSelectedVideo(
            null
          )
        }
        className={
          classes.modal
        }
      >
        <div
          className={
            classes.modalContent
          }
        >
          <div
            className={
              classes.modalHeader
            }
          >
            <Typography
              variant="h6"
            >
              {selectedVideo?.title ||
                "Vídeo de ajuda"}
            </Typography>

            <IconButton
              onClick={() =>
                setSelectedVideo(
                  null
                )
              }
            >
              <Close />
            </IconButton>
          </div>

          {selectedVideo?.video && (
            <div
              className={
                classes.videoWrapper
              }
            >
              <iframe
                className={
                  classes.iframe
                }
                src={
                  `https://www.youtube.com/embed/${selectedVideo.video}`
                }
                title={
                  selectedVideo.title ||
                  "Vídeo de ajuda"
                }
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </Modal>
    </MainContainer>
  );
};

export default Helps;
